import { defineStore } from 'pinia'

export interface AISettings {
  apiKey: string
  apiUrl: string
  model: string
  temperature: number
  maxTokens: number
}

export type StorageType = 'indexeddb' | 'opfs' | 'memory'
export type WorkerType = 'dedicated'

// 性能测量工具
const createPerformanceMeasure = (name: string) => {
  const startTime = performance.now()
  return {
    end: () => {
      const endTime = performance.now()
      const duration = endTime - startTime
      console.log(`🚀 设置性能 [${name}]: ${duration.toFixed(2)}ms`)
      return duration
    }
  }
}

export const useSettingsStore = defineStore('settings', () => {
  const aiSettings = ref<AISettings>({
    // apiKey: 'sk-fXP3Ro6PizDuBZH_BqIh3AwO5izLM8Gtbi1xttPU6JIqTaFe42Dw1I2aU14',
    apiKey: 'AIzaSyCT2Jt04DGxjDdppgHI6V4C6EzufIqlS3A',
    apiUrl: 'https://generativelanguage.googleapis.com/v1beta/openai',
    model: 'gemini-2.5-pro',
    temperature: 0.95,
    maxTokens: 163840  // 增加默认值以支持更长的上下文，特别是 Gemini 等模型
  })

  const sidebarWidth = ref(280)
  const theme = ref<'light' | 'dark'>('light')
  const workerType = ref<WorkerType>('dedicated') // 使用 PGliteWorker 多标签页模式
  const advancedMode = ref(true) // 高级模式开关
  
  // 获取环境变量默认存储类型
  const getDefaultStorageType = (): StorageType => {
    const configMeasure = createPerformanceMeasure('获取默认存储类型')
    try {
      const runtimeConfig = useRuntimeConfig()
      const envStorageType = runtimeConfig.public?.PGLiteStorage
      if (envStorageType && ['indexeddb', 'opfs', 'memory'].includes(envStorageType)) {
        configMeasure.end()
        return envStorageType as StorageType
      }
    } catch (error) {
      console.warn('Unable to get runtime config, using indexeddb as default')
    }
    configMeasure.end()
    // 默认使用IndexedDB，因为OPFS初始化较慢
    return 'indexeddb'
  }
  
  const storageType = ref<StorageType>(getDefaultStorageType())

  // 从 localStorage 加载设置
  const loadSettings = () => {
    if (typeof window !== 'undefined') {
      const loadMeasure = createPerformanceMeasure('从localStorage加载设置')
      const savedSettings = localStorage.getItem('chatgpt-clone-settings')
      if (savedSettings) {
        try {
          const parseMeasure = createPerformanceMeasure('解析设置JSON')
          const parsed = JSON.parse(savedSettings)
          parseMeasure.end()
          
          const applyMeasure = createPerformanceMeasure('应用设置')
          aiSettings.value = { ...aiSettings.value, ...parsed.aiSettings }
          sidebarWidth.value = parsed.sidebarWidth || 280
          theme.value = parsed.theme || 'light'
          workerType.value = parsed.workerType || 'dedicated'
          advancedMode.value = parsed.advancedMode || false
          
          // 如果用户之前有保存过存储类型选择，使用用户的选择
          // 否则使用环境变量的默认值
          if (parsed.storageType && ['indexeddb', 'opfs', 'memory'].includes(parsed.storageType)) {
            storageType.value = parsed.storageType
          } else {
            // 用户第一次访问，使用环境变量默认值
            storageType.value = getDefaultStorageType()
            console.log('🔧 首次访问，使用环境变量默认存储类型:', storageType.value)
          }
          applyMeasure.end()
        } catch (error) {
          console.error('❌ Failed to load settings:', error)
          // 解析失败时，使用环境变量默认值
          storageType.value = getDefaultStorageType()
        }
      } else {
        // 没有保存的设置，使用环境变量默认值
        storageType.value = getDefaultStorageType()
        console.log('🔧 没有保存的设置，使用环境变量默认存储类型:', storageType.value)
      }
      loadMeasure.end()
    }
  }

  // 保存设置到 localStorage
  const saveSettings = () => {
    if (typeof window !== 'undefined') {
      const saveMeasure = createPerformanceMeasure('保存设置到localStorage')
      const stringifyMeasure = createPerformanceMeasure('序列化设置JSON')
      const settings = {
        aiSettings: aiSettings.value,
        sidebarWidth: sidebarWidth.value,
        theme: theme.value,
        storageType: storageType.value,
        workerType: workerType.value,
        advancedMode: advancedMode.value
      }
      const settingsString = JSON.stringify(settings)
      stringifyMeasure.end()
      
      const localStorageMeasure = createPerformanceMeasure('写入localStorage')
      localStorage.setItem('chatgpt-clone-settings', settingsString)
      localStorageMeasure.end()
      
      saveMeasure.end()
      console.log('✅ 设置已保存到localStorage，存储类型:', storageType.value)
    }
  }

  // 更新 AI 设置
  const updateAISettings = (newSettings: Partial<AISettings>) => {
    const updateMeasure = createPerformanceMeasure('更新AI设置')
    aiSettings.value = { ...aiSettings.value, ...newSettings }
    saveSettings()
    updateMeasure.end()
  }

  // 更新侧边栏宽度
  const updateSidebarWidth = (width: number) => {
    const updateMeasure = createPerformanceMeasure('更新侧边栏宽度')
    const maxWidth = window.innerWidth
    sidebarWidth.value = Math.max(0, Math.min(maxWidth, width))
    saveSettings()
    updateMeasure.end()
  }

  // 更新存储类型
  const updateStorageType = (type: StorageType) => {
    const updateMeasure = createPerformanceMeasure('更新存储类型')
    storageType.value = type
    saveSettings()
    updateMeasure.end()
    console.log('✅ 存储类型已更新并保存:', type)
  }

  // 更新 Worker 类型
  const updateWorkerType = (type: WorkerType) => {
    const updateMeasure = createPerformanceMeasure('更新Worker类型')
    workerType.value = type
    saveSettings()
    updateMeasure.end()
    console.log('✅ Worker类型已更新并保存:', type)
  }

  // 更新高级模式
  const updateAdvancedMode = (enabled: boolean) => {
    const updateMeasure = createPerformanceMeasure('更新高级模式')
    advancedMode.value = enabled
    saveSettings()
    updateMeasure.end()
    console.log('✅ 高级模式已更新并保存:', enabled)
  }

  // 切换主题
  const toggleTheme = () => {
    const toggleMeasure = createPerformanceMeasure('切换主题')
    theme.value = theme.value === 'light' ? 'dark' : 'light'
    saveSettings()
    toggleMeasure.end()
  }

  // 监听设置变化自动保存（除了storageType、workerType和advancedMode，因为它们由专门的更新函数处理）
  watch([aiSettings, sidebarWidth, theme], saveSettings, { deep: true })

  return {
    aiSettings: readonly(aiSettings),
    sidebarWidth: readonly(sidebarWidth),
    theme: readonly(theme),
    storageType: readonly(storageType),
    workerType: readonly(workerType),
    advancedMode: readonly(advancedMode),
    loadSettings,
    saveSettings,
    updateAISettings,
    updateSidebarWidth,
    updateStorageType,
    updateWorkerType,
    updateAdvancedMode,
    toggleTheme,
    getDefaultStorageType
  }
})